/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_assignment_sides-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_assignment_sides-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_assignment_sides-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, name, code, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_assignment_sides-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_assignment_sides-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_assignment_sides.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_assignment_sides-entity-config.ts
 * Public fields: 6/6 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupAssignmentSidesClient, LookupAssignmentSidesQueryOptions } from '../generated_clients/generated-lookup_assignment_sides-api-client.js';
import {
  LookupAssignmentSidesInsert,
  LookupAssignmentSidesUpdate,
  LookupAssignmentSidesFieldConfig
} from '../../types/generated_schemas/generated-lookup_assignment_sides-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_assignment_sides_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupAssignmentSidesKeys = {
  all: ['lookup_assignment_sides'] as const,
  lists: () => [...lookupAssignmentSidesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupAssignmentSidesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupAssignmentSidesKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupAssignmentSidesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupAssignmentSidesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupAssignmentSidesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupAssignmentSidesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupAssignmentSidesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupAssignmentSidesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupAssignmentSidesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupAssignmentSidesClient();
};

/**
 * Hook to get all lookup_assignment_sides with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_assignment_sides data
 */
export const useLookupAssignmentSidesList = (options?: LookupAssignmentSidesQueryOptions) => {
  const client = useLookupAssignmentSidesClient();
  
  return useQuery({
    queryKey: lookupAssignmentSidesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_assignment_side by ID
 *
 * @param id - The lookup_assignment_side ID
 * @returns React Query result with single lookup_assignment_side data
 */
export const useLookupAssignmentSides = (id: string) => {
  const client = useLookupAssignmentSidesClient();
  
  return useQuery({
    queryKey: lookupAssignmentSidesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_assignment_sides
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_assignment_sides_business_hooks/
 */

/**
 * Hook to create a new lookup_assignment_side
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, name, code, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_assignment_side
 */
export const useCreateLookupAssignmentSides = () => {
  const client = useLookupAssignmentSidesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupAssignmentSidesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupAssignmentSidesKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_assignment_side
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_assignment_side
 */
export const useUpdateLookupAssignmentSides = () => {
  const client = useLookupAssignmentSidesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupAssignmentSidesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupAssignmentSidesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupAssignmentSidesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_assignment_side
 *
 * @returns React Query mutation for deleting lookup_assignment_side
 */
export const useDeleteLookupAssignmentSides = () => {
  const client = useLookupAssignmentSidesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupAssignmentSidesKeys.all });
    },
  });
};

/**
 * Hook to count lookup_assignment_sides with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupAssignmentSidesCount = (filter?: Partial<Record<typeof LookupAssignmentSidesFieldConfig.filterable[number], any>>) => {
  const client = useLookupAssignmentSidesClient();
  
  return useQuery({
    queryKey: lookupAssignmentSidesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupAssignmentSidesFieldConfig = () => {
  return LookupAssignmentSidesFieldConfig;
};